import React, { useRef, useEffect } from 'react';
import { Box } from '@withjoy/joykit';
import { useTranslation } from '@shared/core/i18n/useTranslation';
import { useSwipeActionContext } from '../SwipeAction.provider';
import type { SwipeActionContentProps } from '../SwipeAction.types';

const VISUALLY_HIDDEN_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export const SwipeActionContent: React.FC<SwipeActionContentProps> = ({ backgroundColor = 'white', children }) => {
  const { tq } = useTranslation('sharedComponents');
  const { isOpen, disabled, contentRef, actionsRef, open, close } = useSwipeActionContext();
  const toggleRef = useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  };

  // Focus management: when actions open via keyboard, focus first action button.
  // Skip for touch swipe — programmatic focus on mobile interferes with the next tap.
  useEffect(() => {
    if (isOpen && actionsRef.current && document.activeElement === toggleRef.current) {
      const firstButton = actionsRef.current.querySelector<HTMLButtonElement>('button');
      firstButton?.focus();
    }
  }, [isOpen, actionsRef]);

  // Focus management: when actions close, return focus to toggle
  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    if (prevIsOpenRef.current && !isOpen && toggleRef.current) {
      toggleRef.current.focus();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  // Close on Escape key (standard accessibility pattern for dismissible overlays)
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  // Handle click on content area to close when open
  const handleContentClick = () => {
    if (isOpen) {
      close();
    }
  };

  return (
    <Box
      position="relative"
      zIndex={1}
      ref={contentRef}
      onClick={handleContentClick}
      backgroundColor={backgroundColor}
    >
      {children}
      {!disabled && (
        <>
          <button
            ref={toggleRef}
            type="button"
            style={VISUALLY_HIDDEN_STYLE}
            aria-expanded={isOpen}
            aria-label={isOpen ? tq`Hide actions` : tq`Show actions`}
            onClick={e => {
              e.stopPropagation();
              handleToggle();
            }}
          />
          <div
            aria-live="polite"
            style={VISUALLY_HIDDEN_STYLE}
            role="status"
          >
            {isOpen ? tq`Actions revealed` : ''}
          </div>
        </>
      )}
    </Box>
  );
};
